import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, inject } from '@angular/core';
import { TransformComponent } from 'projects/visualize3d/src/lib/v3d-components/transform-component';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { validateNumber } from '../../validators/numeric-input.validator';
import {
  Vector3,
  getScale,
  setScale,
  getPosition,
  setPosition,
  getRotationMatrix,
  getRotation,
  setRotation,
  toEulerAngles,
  fromEuler,
} from 'projects/visualize3d/src/public-api';
import { FormParser } from '../../providers/form-parser.service';

type VectorForm = {
  x: FormControl<number>;
  y: FormControl<number>;
  z: FormControl<number>;
};

type TransformForm = {
  position: FormGroup<VectorForm>;
  rotation: FormGroup<VectorForm>;
  scale: FormGroup<VectorForm>;
};

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-transform-component-editor',
  templateUrl: './transform-component-editor.component.html',
  styleUrls: ['./transform-component-editor.component.css'],
  standalone: false,
})
export class TransformComponentEditorComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private parser = inject(FormParser);

  @Input() component: TransformComponent;
  @Output() componentChange = new EventEmitter();

  transformForm: FormGroup<TransformForm>;

  constructor() {
    this.transformForm = this.fb.group({
      position: this.fb.group({
        x: [0, validateNumber],
        y: [0, validateNumber],
        z: [0, validateNumber],
      }),
      rotation: this.fb.group({
        x: [0, validateNumber],
        y: [0, validateNumber],
        z: [0, validateNumber],
      }),
      scale: this.fb.group({
        x: [0, validateNumber],
        y: [0, validateNumber],
        z: [0, validateNumber],
      }),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.component) {
      this.setFormValuesFrom(changes.component.currentValue);
    }
  }

  onSubmit(): void {
    const positionGroup = this.transformForm.controls.position;
    const position = {
      x: this.parser.parseNumber(positionGroup.controls.x),
      y: this.parser.parseNumber(positionGroup.controls.y),
      z: this.parser.parseNumber(positionGroup.controls.z),
    };
    setPosition(this.component.matrix, position);
    const scaleGroup = this.transformForm.controls.scale;
    const scale = {
      x: this.parser.parseNumber(scaleGroup.controls.x),
      y: this.parser.parseNumber(scaleGroup.controls.y),
      z: this.parser.parseNumber(scaleGroup.controls.z),
    };
    const rotationGroup = this.transformForm.controls.rotation;
    setScale(this.component.matrix, scale);
    const eulerAngles: Vector3 = {
      x: this.parser.parseNumber(rotationGroup.controls.x),
      y: this.parser.parseNumber(rotationGroup.controls.y),
      z: this.parser.parseNumber(rotationGroup.controls.z),
    };
    setRotation(this.component.matrix, fromEuler(eulerAngles));
    this.componentChange.emit();
  }

  private setFormValuesFrom(transform: TransformComponent): void {
    const position = getPosition(transform.matrix);
    const rotation = toEulerAngles(getRotation(getRotationMatrix(transform.matrix)));
    const scale = getScale(transform.matrix);
    this.transformForm.setValue(
      {
        position,
        rotation,
        scale,
      },
      { emitEvent: false },
    );
  }
}
